import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from '@shared/components/ui/navigation-menu';
import { cn } from '@shared/lib/utils';
import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { PageSubTabs } from './page-sub-tabs';

export interface Tab {
  title: string;
  icon?: React.ReactNode;
  href: string;
  disabled?: boolean;
  hide?: boolean;
  subTabs?: SubTab[];
  customElement?: React.ReactNode;
}

export interface SubTab extends Tab {}

export interface PageTabs {
  tabs: Tab[];
  className?: string;
}

export function PageTabs({ tabs, className }: PageTabs) {
  const location = useLocation();
  const [subTabs, setSubTabs] = useState<Tab[]>();

  const setSubTabsFromLocation = useCallback(() => {
    const currentMainTab = tabs.find((tab) => {
      const subTab = tab.subTabs?.find((subTab) =>
        location.pathname.startsWith(subTab.href)
      );
      return location.pathname === tab.href || subTab;
    });

    if (!currentMainTab) {
      return;
    }

    if (currentMainTab.subTabs) {
      setSubTabs(currentMainTab.subTabs);
      return;
    }

    setSubTabs(undefined);
  }, [location.pathname, tabs]);

  useEffect(() => {
    setSubTabsFromLocation();
  }, [setSubTabsFromLocation]);

  const isSubTabActive = useCallback(
    (tab: Tab) => {
      if (!tab.subTabs) {
        return false;
      }

      return tab.subTabs.some((subTab) => {
        return location.pathname.includes(subTab.href);
      });
    },
    [location.pathname]
  );

  return (
    <div className={cn('w-full', className)}>
      <NavigationMenu className="z-[1] w-full max-w-none justify-start  overflow-y-auto">
        <NavigationMenuList className="space-x-0">
          {tabs
            .filter((tab) => !tab.hide)
            .map((tab) => (
              <NavigationMenuItem key={tab.title}>
                <NavLink
                  to={tab.href}
                  data-testid={`tab-${tab.title}`}
                  onClick={(e) => tab.disabled && e.preventDefault()}
                  className={({ isActive }) => {
                    const active =
                      isActive ||
                      isSubTabActive(tab) ||
                      location.pathname === tab.href;
                    return cn(
                      'border-b-2 font-medium text-nowrap text-sm px-4 py-2.5 transition-all ease-in duration-150 flex flex-row items-center',
                      {
                        ' text-teal-700 border-b-teal-600': active,
                        ' hover:text-gray-700 hover:border-b-teal-300': !active,
                        'hover: cursor-default hover:text-gray-300 hover:border-b-none text-gray-300 ':
                          tab.disabled,
                      }
                    );
                  }}
                  end={!tab.subTabs}
                >
                  {tab.customElement ? (
                    tab.customElement
                  ) : (
                    <>
                      {tab.title}
                      {tab.icon && (
                        <div className="ml-1.5 flex h-[20px] items-center">
                          {tab.icon}
                        </div>
                      )}
                    </>
                  )}
                </NavLink>
              </NavigationMenuItem>
            ))}
        </NavigationMenuList>
      </NavigationMenu>
      {subTabs && <PageSubTabs tabs={subTabs} />}
    </div>
  );
}
